/**
 * Top-level domain navigation modules (Oracle-style bar).
 * Route keys mirror contextual sidebar entries in DashboardLayout.
 */

export type NavModuleKey =
  | 'home'
  | 'sales'
  | 'scm'
  | 'mfg'
  | 'asset'
  | 'fin'
  | 'qms'
  | 'prj'
  | 'epc'
  | 'hr'
  | 'edu'
  | 'analytics'
  | 'ownerIntel'
  | 'platform';

export interface NavModuleDefinition {
  key: NavModuleKey;
  label: string;
  landingRoute: string;
  routeKeys: string[];
  /**
   * Top nav tab is shown only when the user can access ANY of these routes.
   * Prevents incidental access (e.g. company.view) from revealing the whole module.
   */
  moduleTabGateRoutes?: string[];
}

/** Modules where operational assignment is not modeled — permission gate only. */
export const OPERATIONAL_FALLBACK_MODULES: NavModuleKey[] = [
  'fin',
  'qms',
  'asset',
  'analytics',
  'ownerIntel',
  'platform',
  'edu',
];

export const NAV_MODULE_DEFINITIONS: NavModuleDefinition[] = [
  {
    key: 'home',
    label: 'Home',
    landingRoute: '/dashboard',
    routeKeys: [
      '/dashboard',
      '/home/tasks',
      '/home/approvals',
      '/home/team-tasks',
      '/home/drafts',
      '/home/alerts',
      '/home/company-overview',
      '/unified',
      '/home/exceptions',
      '/reports',
      '/reports/audit',
      '/help',
    ],
  },
  {
    key: 'edu',
    label: 'Education',
    landingRoute: '/education',
    routeKeys: [
      '/education',
      '/education/workspace',
      '/education/settings',
      '/education/students',
      '/education/admissions',
      '/education/admissions/crm',
      '/education/admissions/enquiries',
      '/education/enrollments',
      '/education/progression',
      '/education/attendance',
      '/education/attendance/overview',
      '/education/attendance/exceptions',
      '/education/attendance/settings',
      '/education/attendance/calendar',
      '/education/attendance/leave',
      '/education/attendance/corrections',
      '/education/transfer-certificates',
      '/education/reports',
      '/education/behaviour',
      '/education/inclusion',
      '/education/intervention',
      '/education/standards',
      '/education/governance',
      '/education/app',
      '/education/eqams',
      '/education/portal/parent',
      '/education/portal/student',
      '/education/portal/teacher',
      '/education/udise',
      '/education/ids',
      '/education/comms',
      '/education/print',
      '/education/pulse',
      '/education/quality-copilot',
      '/education/process-insights',
      '/education/fees',
      '/education/fees/collection',
      '/education/fees/family-statement',
      '/education/fees/promises',
      '/education/fees/disputes',
      '/education/transport',
      '/education/transport/boarding',
      '/education/library',
      '/education/library/settings',
      '/education/library/reservations',
      '/education/assessments',
      '/education/questions',
      '/education/question-blueprints',
      '/education/hpc',
      '/education/lms',
      '/education/activities',
      '/education/activities/achievements',
      '/education/activities/meetings',
      '/education/timetable',
      '/education/substitutions',
      '/education/curriculum',
      '/education/academic',
      '/education/policy',
      '/education/health',
      '/education/counseling',
      '/education/safeguarding',
    ],
  },
  {
    key: 'sales',
    label: 'Growth & Revenue',
    landingRoute: '/growth/dashboard',
    routeKeys: [
      '/growth/dashboard',
      '/growth/leads',
      '/growth/opportunities',
      '/growth/calendar',
      '/growth/activities',
      '/growth/follow-ups',
      '/growth/standup',
      '/growth/standup/team',
      '/growth/standup/history',
      '/growth/demos',
      '/growth/setup/activity-types',
      '/growth/sales-targets',
      '/growth/reports/weekly-sales-performance',
      '/growth/order-review-checklists',
      '/growth/quotations',
      '/growth/setup/quotation-defaults',
      '/growth/contract-reviews',
      '/growth/estimations/boq',
      '/growth/estimations/assembly',
      '/growth/estimations/trading-rental',
      '/growth/quotation-consolidation',
      '/growth/quotation-conversion-reports',
      '/growth/reports',
      '/growth/reports/tre-overhead',
      '/growth/reports/project-costing',
      '/growth/reports/invoice-register',
      '/growth/reports/order-register',
      '/growth/reports/delivery-note-register',
      '/growth/reports/customer-analysis',
      '/growth/reports/invoice-aging',
      '/growth/reports/opportunity-to-fulfillment',
      '/growth/reports/conversion-funnel',
      '/growth/reports/win-loss',
      '/growth/reports/opportunity-conversion',
      '/growth/reports/salesperson-performance',
      '/growth/reports/product-rst-mix',
      '/growth/reports/booking-billing-collections',
      '/growth/reports/quotation-aging',
      '/growth/reports/opportunity-aging',
      '/growth/reports/credit-note-register',
      '/growth/reports/opportunity-register',
      '/growth/reports/quotation-list-register',
      '/growth/orders',
      '/growth/order-timesheets',
      '/growth/order-health',
      '/growth/inland-process-deviations',
      '/growth/invoices',
      '/growth/delivery-notes',
      '/growth/document-collections',
      '/growth/delivery-readiness',
      '/growth/my-deliveries',
      '/growth/packing-list',
      '/growth/customers',
      '/growth/customer-success',
      '/cspm',
      '/platform/foundation/revenue-service-types',
      '/mfg/operations',
    ],
  },
  {
    key: 'scm',
    label: 'Supply Chain',
    landingRoute: '/scm/dashboard',
    routeKeys: [
      '/scm/dashboard',
      '/scm/reports',
      '/scm/reports/purchase-register',
      '/scm/reports/grn-register',
      '/scm/reports/supplier-invoice-register',
      '/scm/reports/purchase-return-register',
      '/scm/reports/purchase-requisition-register',
      '/scm/reports/po-aging',
      '/scm/reports/supplier-analysis',
      '/scm/reports/service-receipt-register',
      '/scm/reports/stock-movement',
      '/scm/reports/material-request-register',
      '/scm/reports/material-issue-register',
      '/scm/reports/inventory-valuation',
      '/scm/reports/gl-reconciliation',
      '/scm/reports/project-material-reconciliation',
      '/scm/reports/stock-aging',
      '/scm/reports/abc-analysis',
      '/scm/reports/dead-stock',
      '/scm/reports/purchase-price-variance',
      '/scm/reports/demand-planning',
      '/scm/reports/landed-cost-revalue',
      '/scm/procurement/suppliers',
      '/scm/procurement/control-tower',
      '/scm/procurement/supplier-categories',
      '/scm/procurement/purchase-requests',
      '/scm/procurement/spot-purchase',
      '/scm/procurement/supplier-inquiries',
      '/scm/procurement/supplier-quotations',
      '/scm/procurement/bid-analyses',
      '/scm/procurement/purchase-orders',
      '/scm/procurement/grn',
      '/scm/procurement/receiving-inspections',
      '/scm/procurement/service-receipts',
      '/scm/procurement/reports/po-milestones',
      '/scm/procurement/supplier-invoices',
      '/scm/procurement/three-way-matching',
      '/scm/procurement/purchase-returns',
      '/scm/warehouse/warehouses',
      '/scm/warehouse/stock-transfer',
      '/scm/warehouse/stock-adjustment',
      '/scm/inventory/items',
      '/scm/inventory/item-categories',
      '/scm/inventory/uom-units',
      '/scm/inventory/engineering',
      '/inventory/item-classification-gl-accounts',
      '/scm/inventory/material-requests',
      '/scm/inventory/site-materials',
      '/scm/inventory/stock-summary',
      '/scm/inventory/heat-stock',
      '/scm/inventory/stock-count',
      '/scm/inventory/putaway',
      '/scm/inventory/consignment-receipts',
      '/scm/inventory/consignment-returns',
      '/scm/inventory/opening-stock',
      '/scm/quality/criticality-risk',
      '/scm/quality/supplier-assessments',
      '/scm/quality/supplier-assessments/new',
      '/scm/quality/approved-supplier-list',
      '/scm/quality/performance-monitoring',
      '/scm/quality/critical-mtc',
    ],
  },
  {
    key: 'mfg',
    label: 'Manufacturing',
    landingRoute: '/mfg/control-tower',
    routeKeys: [
      '/mfg/control-tower',
      '/mfg/workstation-control-center',
      '/mfg/dashboard',
      '/mfg/production-requests',
      '/mfg/work-orders',
      '/mfg/work-order-operations',
      '/mfg/work-order-operations/eod-close',
      '/mfg/factory-floor',
      '/mfg/route-sheet-register',
      '/mfg/traceability-matrix',
      '/mfg/boms',
      '/mfg/workstations',
      '/mfg/stage-master',
      '/mfg/calendar',
      '/mfg/capacity-planning',
      '/mfg/material-audit',
      '/mfg/master-schedule',
      '/mfg/reports',
      '/mfg/reports/work-order-register',
      '/mfg/reports/operations-status',
      '/mfg/reports/outsourced-fabrication',
    ],
  },
  {
    key: 'asset',
    label: 'Assets',
    landingRoute: '/asset/assets',
    routeKeys: [
      '/asset/assets',
      '/asset/reports',
      '/asset/scan',
      '/asset/maintenance',
      '/asset/depreciation',
      '/asset/disposal',
      '/asset/accounting-defaults',
      '/asset/groups',
      '/asset/insurance',
      '/asset/audit',
      '/asset/audit/report',
      '/asset/pmv/hub',
      '/asset/pmv/control-tower',
      '/asset/pmv',
      '/asset/pmv/components',
      '/asset/pmv/components/control-tower',
      '/asset/pmv/components/import',
      '/asset/pmv/fuel',
      '/asset/pmv/fuel/control-tower',
      '/asset/pmv/fuel/reconciliation',
      '/asset/pmv/fuel/attention',
      '/asset/pmv/fuel/sources',
      '/asset/pmv/reliability',
      '/asset/pmv/reliability/control-tower',
      '/asset/pmv/reliability/attention',
      '/asset/pmv/reliability/failures',
      '/asset/pmv/reliability/faults',
      '/asset/pmv/reliability/predictions',
      '/asset/pmv/reliability/forecast',
      '/asset/pmv/reliability/conditions',
      '/asset/pmv/connected',
      '/asset/pmv/connected/devices',
      '/asset/pmv/connected/providers',
      '/asset/pmv/connected/reconciliation',
      '/asset/pmv/connected/diagnostics',
      '/asset/pmv/setup',
      '/asset/pmv/operations',
      '/asset/pmv/requests',
      '/asset/pmv/daily-logs',
    ],
  },
  {
    key: 'fin',
    label: 'Finance',
    landingRoute: '/fin/dashboard',
    moduleTabGateRoutes: ['/fin/dashboard'],
    routeKeys: [
      '/fin/dashboard',
      '/fin/gl/chart-of-accounts',
      '/fin/gl/journals',
      '/fin/receipt-vouchers',
      '/fin/proforma-invoices',
      '/fin/payment-vouchers',
      '/finance/ap/supplier-advance-refunds',
      '/fin/direct-purchase-invoices',
      '/fin/prepaid',
      '/fin/debit-notes',
      '/fin/credit-notes',
      '/fin/currencies',
      '/fin/tax/vat-return',
      '/fin/departments',
      '/fin/payment-terms',
      '/fin/banking/accounts',
      '/fin/reports',
      '/fin/reports/monthly-profit-loss',
      '/fin/reports/operational-profit',
      '/fin/reports/ledger',
      '/fin/reports/project-ledger',
      '/fin/reports/project-profit-loss',
      '/fin/reports/customer-outstanding',
      '/fin/reports/supplier-outstanding',
      '/fin/reports/customer-statement',
      '/fin/reports/supplier-statement',
      '/fin/reports/settlement-register',
      '/fin/reports/direct-purchase-invoice-register',
      '/fin/intercompany/affiliates',
      '/fin/intercompany/transactions',
      '/fin/intercompany/settlements',
      '/fin/intercompany/groups',
      '/fin/reports/intercompany-balance',
      '/fin/reports/intercompany-statement',
      '/fin/reports/intercompany-consolidation',
    ],
  },
  {
    key: 'qms',
    label: 'Quality',
    landingRoute: '/qms/dashboard',
    routeKeys: [
      '/qms/dashboard',
      '/qms/quality-plans',
      '/qms/inspections',
      '/qms/receiving-dimensional-inspections',
      '/qms/initial-inspection-requests',
      '/qms/final-inspection-reports',
      '/qms/inspection-release-notes',
      '/qms/certificates-of-conformity',
      '/qms/tmmde',
      '/qms/tmmde/impact',
      '/qms/dimensional-inspection',
      '/qms/ncrs',
      '/qms/capas',
      '/qms/audits',
      '/grc/risk-assessments',
      '/grc/moc',
      '/grc/moc/reports',
      '/grc/competency',
      '/grc/competency/schedule',
      '/grc/competency/sessions',
      '/grc/competency/assessments',
      '/grc/calibration',
      '/compliance/uae-einvoicing',
      '/compliance/inbound-einvoices',
      '/compliance/ksa-zatca',
    ],
  },
  {
    key: 'prj',
    label: 'Projects',
    landingRoute: '/prj/projects',
    routeKeys: ['/prj/projects', '/prj/budgets', '/prj/project-health', '/prj/timesheets', '/prj/costs', '/prj/dashboard'],
  },
  {
    key: 'epc',
    label: 'EPC',
    landingRoute: '/epc',
    routeKeys: ['/epc', '/prj/budgets', '/prj/costs', '/prj/timesheets', '/prj/projects'],
  },
  {
    key: 'hr',
    label: 'HRMS',
    landingRoute: '/hr/dashboard',
    routeKeys: [
      '/hr/dashboard',
      '/hr/setup',
      '/hr/accruals',
      '/hr/holidays',
      '/hr/shifts',
      '/hr/locations',
      '/hr/positions',
      '/hr/leave-types',
      '/hr/leave-opening-balances',
      '/hr/grades',
      '/hr/salary-heads',
      '/hr/me',
      '/hr/employees',
      '/hr/contracts',
      '/hr/salary-revisions',
      '/hr/profile-change-requests',
      '/hr/allocations',
    '/hr/asset-custody',
    '/hr/recruitment',
    '/hr/requisitions',
    '/hr/jobs',
    '/hr/candidates',
    '/hr/applications',
    '/hr/interviews',
    '/hr/assessments',
    '/hr/offers',
    '/hr/onboarding',
    '/hr/careers-settings',
    '/hr/recruit-reports',
    '/hr/attendance',
      '/hr/overtime',
      '/hr/leaves',
      '/hr/leave-settlements',
      '/hr/adjustments',
      '/hr/loans',
      '/hr/payroll',
      '/hr/eosb',
      '/hr/documents',
      '/hr/document-expiry',
      '/hr/audit-log',
      '/hr/reports',
    ],
  },
  {
    key: 'analytics',
    label: 'Analytics',
    landingRoute: '/analytics/executive',
    moduleTabGateRoutes: ['/analytics/executive'],
    routeKeys: [
      '/analytics/executive',
      '/growth/dashboard?tab=analytics',
      '/analytics/scm',
      '/analytics/mfg',
      '/analytics/quality',
      '/ai/workspace',
      '/ai/udq',
      '/ai/dashboard',
      '/ai/enterprise-memory',
    ],
  },
  {
    key: 'ownerIntel',
    label: 'Owner Intel',
    landingRoute: '/owner-intelligence',
    moduleTabGateRoutes: ['/owner-intelligence'],
    routeKeys: [
      '/owner-intelligence',
      '/owner-intelligence/group',
      '/owner-intelligence/memory',
      '/owner-intelligence/meetings',
      '/owner-intelligence/business-health',
      '/owner-intelligence/financial-health',
      '/owner-intelligence/kpis',
      '/owner-intelligence/benchmarks',
      '/owner-intelligence/profit',
      '/owner-intelligence/cash',
      '/owner-intelligence/customers',
      '/owner-intelligence/sales-pipeline',
      '/owner-intelligence/operations',
      '/owner-intelligence/supply-chain',
      '/owner-intelligence/strategy',
      '/owner-intelligence/enterprise-strategy',
      '/owner-intelligence/enterprise-readiness',
      '/owner-intelligence/actions',
      '/owner-intelligence/root-cause',
      '/owner-intelligence/risk',
      '/owner-intelligence/operational-intelligence',
      '/owner-intelligence/operational-actions',
      '/owner-intelligence/digital-twin',
      '/owner-intelligence/module-intelligence',
      '/owner-intelligence/automation',
      '/owner-intelligence/scenarios',
      '/owner-intelligence/advisor',
      '/owner-intelligence/ai-governance',
      '/owner-intelligence/organizational-knowledge',
      '/owner-intelligence/external-context',
      '/owner-intelligence/board-packs',
    ],
  },
  {
    key: 'platform',
    label: 'Platform',
    landingRoute: '/platform/setup/company',
    moduleTabGateRoutes: [
      '/platform/companies',
      '/platform/setup/system',
      '/platform/setup/status',
      '/platform/security/users',
      '/platform/workflows/approvals',
      '/platform/sales-teams',
      '/platform/ai-agent/config',
    ],
    routeKeys: [
      '/platform/companies',
      '/platform/companies/new',
      '/platform/security/users',
      '/platform/sales-teams',
      '/platform/ai-agent/config',
      '/platform/ai-agent/audit',
      '/platform/workflows/approvals',
      '/platform/workflows/rejection-reasons',
      '/platform/rejection-register',
      '/platform/setup/company',
      '/platform/setup/configuration-studio',
      '/platform/setup/system',
      '/platform/setup/api-keys',
      '/platform/setup/status',
      '/platform/setup/sequences',
      '/platform/setup/responsibility-rules',
      '/platform/setup/email-templates',
      '/platform/setup/task-sla',
      '/platform/documents',
      '/platform/foundation/customers',
      '/platform/foundation/suppliers',
      '/platform/foundation/supplier-categories',
      '/platform/foundation/items',
      '/platform/foundation/warehouses',
      '/platform/foundation/currencies',
      '/platform/foundation/revenue-service-types',
    ],
  },
];

/** Preferred Sales tab landing order (delivery-first for logistics-only users). */
export const SALES_LANDING_ROUTE_PRIORITY: string[] = [
  '/growth/delivery-readiness',
  '/growth/delivery-notes',
  '/growth/my-deliveries',
  '/growth/packing-list',
  '/growth/document-collections',
  '/growth/dashboard',
];

const MODULE_BY_KEY = new Map(NAV_MODULE_DEFINITIONS.map((m) => [m.key, m]));

export function getNavModuleDefinition(key: NavModuleKey): NavModuleDefinition | undefined {
  return MODULE_BY_KEY.get(key);
}

/** First accessible route for a module; falls back to static landingRoute. */
export function getModuleLandingRoute(
  key: NavModuleKey,
  canAccessPath: (path: string) => boolean
): string {
  const def = getNavModuleDefinition(key);
  if (!def) return '/home';

  if (key === 'sales') {
    for (const route of SALES_LANDING_ROUTE_PRIORITY) {
      if (canAccessPath(route)) return route;
    }
  }

  for (const route of def.routeKeys) {
    if (canAccessPath(route)) return route;
  }

  return def.landingRoute;
}

/** Path prefix → nav module key (first match wins). */
export function navModuleKeyFromPath(pathname: string): NavModuleKey | null {
  const path = pathname || '/';
  if (
    path === '/' ||
    path === '/dashboard' ||
    path.startsWith('/dashboard/') ||
    path.startsWith('/home') ||
    path === '/unified' ||
    path.startsWith('/unified/') ||
    path.startsWith('/reports')
  ) {
    return 'home';
  }
  if (path.startsWith('/growth') || path.startsWith('/sales') || path.startsWith('/crm')) return 'sales';
  if (path.startsWith('/scm') || path.startsWith('/procurement') || path.startsWith('/inventory')) return 'scm';
  if (path === '/education' || path.startsWith('/education/')) return 'edu';
  if (path.startsWith('/mfg') || path.startsWith('/manufacturing')) return 'mfg';
  if (path.startsWith('/asset')) return 'asset';
  if (path.startsWith('/fin') || path.startsWith('/finance')) return 'fin';
  if (
    path.startsWith('/qms') ||
    path.startsWith('/grc') ||
    path.startsWith('/quality') ||
    path.startsWith('/compliance')
  ) {
    return 'qms';
  }
  if (path.startsWith('/prj') || path.startsWith('/projects')) return 'prj';
  if (path.startsWith('/epc')) return 'epc';
  if (path.startsWith('/hr')) return 'hr';
  if (path.startsWith('/owner-intelligence')) return 'ownerIntel';
  if (path.startsWith('/analytics')) return 'analytics';
  if (path.startsWith('/platform') || path.startsWith('/settings')) return 'platform';
  return null;
}
